import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate, formatDateRange, formatShortDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface WeekSummary {
  week_number: number
  start_date: string
  end_date: string
  calc_type: number
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  time_pay: number
  flat_rate_total: number
  mileage: number
  mileage_total: number
  expenses: number
  pto_hours: number
  sto_hours: number
}

interface EmployeeSummaryRow {
  user_id: number | null
  name: string
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  pto_hours: number
  sto_hours: number
}

interface Employee {
  id: number
  name: string
}

interface SelectedEmployee {
  id: number
  name: string
  pay_rate: number | null
  worker_type: number
}

interface PendingTimeEntry {
  id: number
  date: string | null
  employee: string | null
  case_id: number | null
  case_name: string | null
  hours: number | null
  type: string | null
  description: string | null
  mileage: number | null
  date_added: string | null
}

interface PendingExpense {
  id: number
  date: string | null
  employee: string | null
  case_id: number | null
  case_name: string | null
  amount: number
  type: string | null
  description: string | null
  paid_to: string | null
  date_added: string | null
}

interface Props {
  weeks: WeekSummary[]
  employee_summaries: EmployeeSummaryRow[]
  start_date: string
  end_date: string
  employees: Employee[]
  selected_employee_id: number | null
  selected_employee: SelectedEmployee | null
  pending: {
    unapproved_time_entries: PendingTimeEntry[]
    unapproved_expenses: PendingExpense[]
  }
}

/* ── Helpers ────────────────────────────────────────────── */

function formatMinutes(minutes: number): string {
  if (minutes === 0) return "-"
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${String(m).padStart(2, "0")}m`
  if (m === 0) return `${h}h`
  return `${h}h ${String(m).padStart(2, "0")}m`
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* ── Pending Columns ──────────────────────────────────── */

const pendingTimeColumns: HpiColumn<PendingTimeEntry>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">
        {row.case_id ? (
          <a
            href={`/cases/${row.case_id}/time_entries/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:underline"
          >
            {formatDate(row.date)}
          </a>
        ) : (
          formatDate(row.date)
        )}
      </div>
    ),
  },
  {
    header: "Employee",
    render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Type",
    render: (row) => <div className="text-sm">{row.type ?? "-"}</div>,
  },
  {
    header: "Description",
    render: (row) => {
      const parts: string[] = []
      if (row.description) parts.push(row.description)
      if (row.mileage && row.mileage > 0) parts.push(`${row.mileage} miles`)
      return (
        <div className="max-w-[200px] truncate text-sm">
          {parts.length > 0 ? parts.join(", ") : "-"}
        </div>
      )
    },
  },
  {
    header: "Hours",
    width: "max-w-[80px]",
    render: (row) => (
      <div className="text-right text-sm">{row.hours?.toFixed(2) ?? "-"}</div>
    ),
  },
  {
    header: "Added",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">{formatDate(row.date_added)}</div>
    ),
  },
]

const pendingExpenseColumns: HpiColumn<PendingExpense>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">
        {row.case_id ? (
          <a
            href={`/cases/${row.case_id}/expenses/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:underline"
          >
            {formatDate(row.date)}
          </a>
        ) : (
          formatDate(row.date)
        )}
      </div>
    ),
  },
  {
    header: "Employee",
    render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Type",
    render: (row) => <div className="text-sm">{row.type ?? "-"}</div>,
  },
  {
    header: "Description",
    render: (row) => {
      const parts: string[] = []
      if (row.description) parts.push(row.description)
      if (row.paid_to) parts.push(row.paid_to)
      return (
        <div className="max-w-[200px] truncate text-sm">
          {parts.length > 0 ? parts.join(", ") : "-"}
        </div>
      )
    },
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm">{formatCurrency(row.amount)}</div>
    ),
  },
  {
    header: "Added",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">{formatDate(row.date_added)}</div>
    ),
  },
]

/* ── Week & Employee Summary Columns (built dynamically) ── */

function buildWeekColumns(
  selectedEmployeeId: number | null,
): HpiColumn<WeekSummary>[] {
  const cols: HpiColumn<WeekSummary>[] = [
    {
      header: "Week",
      width: "w-[200px] !flex-none",
      render: (row) => {
        const label = `Wk ${row.week_number}  ${formatShortDate(row.start_date)}-${formatShortDate(row.end_date)}`
        return (
          <div className="text-sm font-medium">
            <a
              href={`/accounting/payroll/week_detail?start_date=${row.start_date}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {label}
            </a>
          </div>
        )
      },
    },
    {
      header: "OTJ",
      width: "w-[70px] !flex-none",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.otj_minutes)}
        </div>
      ),
    },
    {
      header: "Travel",
      width: "w-[70px] !flex-none",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.travel_minutes)}
        </div>
      ),
    },
    {
      header: "Total Hours",
      width: "w-[80px] !flex-none",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.otj_minutes + row.travel_minutes)}
        </div>
      ),
    },
    {
      header: "Reg",
      width: "w-[70px] !flex-none",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.regular_minutes)}
        </div>
      ),
    },
    {
      header: "OT",
      width: "w-[70px] !flex-none",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.overtime_minutes)}
        </div>
      ),
    },
    {
      header: "Pay",
      width: "w-[100px] !flex-none",
      render: (row) => (
        <div className="text-right text-sm font-medium">
          {formatCurrency(row.time_pay)}
        </div>
      ),
    },
    {
      header: "Miles",
      width: "w-[70px] !flex-none",
      render: (row) => (
        <div className="text-right text-sm">
          {row.mileage > 0 ? row.mileage : "-"}
        </div>
      ),
    },
    {
      header: "Expenses",
      width: "w-[90px] !flex-none",
      render: (row) => (
        <div className="text-right text-sm">
          {row.expenses > 0 ? formatCurrency(row.expenses) : "-"}
        </div>
      ),
    },
  ]

  if (selectedEmployeeId) {
    cols.push({
      header: "Subtotal",
      width: "w-[100px] !flex-none",
      render: (row) => {
        const subtotal =
          row.time_pay + row.flat_rate_total + row.mileage_total + row.expenses
        return (
          <div className="text-right text-sm font-bold">
            {formatCurrency(subtotal)}
          </div>
        )
      },
    })
  }

  return cols
}

function buildEmployeeSummaryColumns(
  startDate: string,
  endDate: string,
): HpiColumn<EmployeeSummaryRow>[] {
  return [
    {
      header: "Name",
      width: "w-[200px] !flex-none",
      render: (row) => (
        <div className="text-sm font-medium">
          {row.user_id ? (
            <a
              href={`/accounting/payroll/history?employee_id=${row.user_id}&start_date=${startDate}&end_date=${endDate}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {row.name}
            </a>
          ) : (
            row.name
          )}
        </div>
      ),
    },
    {
      header: "OTJ",
      width: "w-[70px] !flex-none",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.otj_minutes)}
        </div>
      ),
    },
    {
      header: "Travel",
      width: "w-[70px] !flex-none",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.travel_minutes)}
        </div>
      ),
    },
    {
      header: "Total Hours",
      width: "w-[80px] !flex-none",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.otj_minutes + row.travel_minutes)}
        </div>
      ),
    },
    {
      header: "Reg",
      width: "w-[70px] !flex-none",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.regular_minutes)}
        </div>
      ),
    },
    {
      header: "OT",
      width: "w-[70px] !flex-none",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.overtime_minutes)}
        </div>
      ),
    },
  ]
}

/* ── Totals Rows ──────────────────────────────────────── */

function EmployeeSummaryTotals({
  summaries,
}: {
  summaries: EmployeeSummaryRow[]
}) {
  const totals = summaries.reduce(
    (acc, s) => ({
      otj_minutes: acc.otj_minutes + s.otj_minutes,
      travel_minutes: acc.travel_minutes + s.travel_minutes,
      regular_minutes: acc.regular_minutes + s.regular_minutes,
      overtime_minutes: acc.overtime_minutes + s.overtime_minutes,
    }),
    {
      otj_minutes: 0,
      travel_minutes: 0,
      regular_minutes: 0,
      overtime_minutes: 0,
    },
  )

  return (
    <div className="border-hpi-dark-gray/30 flex items-start gap-3 border-t-2 bg-white px-4 py-3">
      <div className="w-[200px] flex-none">
        <div className="text-sm font-bold">Total</div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.otj_minutes)}
        </div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.travel_minutes)}
        </div>
      </div>
      <div className="w-[80px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.otj_minutes + totals.travel_minutes)}
        </div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.regular_minutes)}
        </div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.overtime_minutes)}
        </div>
      </div>
    </div>
  )
}

function WeeklyTotals({
  weeks,
  showSubtotal,
}: {
  weeks: WeekSummary[]
  showSubtotal: boolean
}) {
  const totals = weeks.reduce(
    (acc, w) => ({
      otj_minutes: acc.otj_minutes + w.otj_minutes,
      travel_minutes: acc.travel_minutes + w.travel_minutes,
      regular_minutes: acc.regular_minutes + w.regular_minutes,
      overtime_minutes: acc.overtime_minutes + w.overtime_minutes,
      time_pay: acc.time_pay + w.time_pay,
      mileage: acc.mileage + w.mileage,
      expenses: acc.expenses + w.expenses,
      subtotal:
        acc.subtotal +
        w.time_pay +
        w.flat_rate_total +
        w.mileage_total +
        w.expenses,
    }),
    {
      otj_minutes: 0,
      travel_minutes: 0,
      regular_minutes: 0,
      overtime_minutes: 0,
      time_pay: 0,
      mileage: 0,
      expenses: 0,
      subtotal: 0,
    },
  )

  return (
    <div className="border-hpi-dark-gray/30 flex items-start gap-3 border-t-2 bg-white px-4 py-3">
      <div className="w-[200px] flex-none">
        <div className="text-sm font-bold">Total</div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.otj_minutes)}
        </div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.travel_minutes)}
        </div>
      </div>
      <div className="w-[80px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.otj_minutes + totals.travel_minutes)}
        </div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.regular_minutes)}
        </div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.overtime_minutes)}
        </div>
      </div>
      <div className="w-[100px] flex-none text-right">
        <div className="text-sm font-bold">
          {formatCurrency(totals.time_pay)}
        </div>
      </div>
      <div className="w-[70px] flex-none text-right">
        <div className="text-sm font-bold">
          {totals.mileage > 0 ? totals.mileage : "-"}
        </div>
      </div>
      <div className="w-[90px] flex-none text-right">
        <div className="text-sm font-bold">
          {totals.expenses > 0 ? formatCurrency(totals.expenses) : "-"}
        </div>
      </div>
      {showSubtotal && (
        <div className="w-[100px] flex-none text-right">
          <div className="text-sm font-bold">
            {formatCurrency(totals.subtotal)}
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Payroll History", href: "/accounting/payroll/history" },
]

export default function PayrollHistory({
  weeks,
  employee_summaries,
  start_date,
  end_date,
  employees,
  selected_employee_id,
  selected_employee,
  pending,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [employeeId, setEmployeeId] = useState<string>(
    selected_employee_id?.toString() ?? "",
  )

  const totalPay = weeks.reduce((sum, w) => sum + w.time_pay, 0)
  const weekColumns = buildWeekColumns(selected_employee_id)
  const empSummaryColumns = buildEmployeeSummaryColumns(start_date, end_date)

  const handleUpdate = () => {
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    }
    if (employeeId) params.employee_id = employeeId
    router.get("/accounting/payroll/history", params, { preserveState: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Payroll History" />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <HpiFilterSelect
            label="Employee"
            value={employeeId}
            onChange={setEmployeeId}
            options={employees.map((e) => ({ value: e.id, label: e.name }))}
            placeholder="All Employees"
          />
        </HpiDateFilterBar>

        {/* Employee header when filtering to single employee (#13, #14) */}
        {selected_employee && (
          <HpiStatsBar
            stats={[
              {
                label: "Employee",
                subLabel: selected_employee.worker_type === 0 ? "W2" : "1099",
                value: selected_employee.name,
              },
              {
                label: "Pay Rate",
                subLabel: "/hr",
                value: selected_employee.pay_rate
                  ? formatCurrency(selected_employee.pay_rate)
                  : "-",
              },
              {
                label: "Period",
                subLabel: "Selected",
                value: formatDateRange(start_date, end_date),
              },
              { label: "Weeks", subLabel: "Shown", value: weeks.length },
              {
                label: "Total",
                subLabel: "Time Pay",
                value: formatCurrency(totalPay),
              },
            ]}
            className="mb-6"
          />
        )}

        {/* Stats bar when viewing all employees */}
        {!selected_employee && (
          <HpiStatsBar
            stats={[
              { label: "Weeks", subLabel: "Shown", value: weeks.length },
              {
                label: "Total",
                subLabel: "Time Pay",
                value: formatCurrency(totalPay),
              },
            ]}
            className="mb-6"
          />
        )}

        {/* Pending time entries (#11) */}
        {pending.unapproved_time_entries.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PendingTimeEntry>
              title={`Unapproved Time Entries (${pending.unapproved_time_entries.length})`}
              columns={pendingTimeColumns}
              data={pending.unapproved_time_entries}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {/* Pending expenses (#11) */}
        {pending.unapproved_expenses.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PendingExpense>
              title={`Unapproved Expenses (${pending.unapproved_expenses.length})`}
              columns={pendingExpenseColumns}
              data={pending.unapproved_expenses}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {/* Employee summary table (#1 - name links) */}
        {employee_summaries.length > 0 && (
          <HpiDataTable<EmployeeSummaryRow>
            title="Summary by Employee"
            columns={empSummaryColumns}
            data={employee_summaries}
            keyFn={(row) => row.user_id ?? "total"}
            className="mb-6"
            pagination={
              <EmployeeSummaryTotals summaries={employee_summaries} />
            }
          />
        )}

        {/* Weekly table (#4-#10) */}
        <HpiDataTable<WeekSummary>
          title="Weekly Payroll History"
          columns={weekColumns}
          data={weeks}
          keyFn={(row) => `${row.week_number}-${row.start_date}`}
          pagination={
            weeks.length > 0 ? (
              <WeeklyTotals
                weeks={weeks}
                showSubtotal={!!selected_employee_id}
              />
            ) : undefined
          }
        />
      </div>
    </AppLayout>
  )
}
